// Community-Tab: Rangliste (Leaderboard)
// Als Dashboard-Tab integriert – kein eigenes Layout.
// NT-KOPFGELD-01: Hauptsortierung KOPFGELD, daneben LEVEL (bisherige Ansicht unverändert).

import { api } from '../services/api.js';
import { navigate } from '../router.js';
import { gameState } from '../state/gameState.js';
import { awardBadgeHtml, type AwardBadge } from '../utils/award-badges.js';
import { renderWantedLevelHtml } from '../utils/wanted-labels.js';
import { openSteckbrief } from '../components/SteckbriefModal.js';
import { UNBESCHRIEBENES_BLATT, focusLabel, focusShortLabel, formatBounty } from '../utils/bounty-labels.js';

// ─── Types (aus Backend-Response) ──────────────────────────────────────────────

type LeaderboardSort = 'bounty' | 'level' | 'achievements';
type LeaderboardFilter = 'all' | 'no_org' | 'seeking';

interface LeaderboardEntry {
  rank: number;
  username: string;
  level: number;
  reputation: number;
  wantedLevel: number;
  prestigeRank: number;
  badges: AwardBadge[];
  isCurrentUser: boolean;
  bounty: number;
  focus: string | null;
  hasOrg?: boolean;
  seekingOrg: boolean;
}

interface LeaderboardResponse {
  leaderboard: LeaderboardEntry[];
  currentUserEntry: LeaderboardEntry | null;
  totalPlayers: number;
  cachedAt: string;
  sort: LeaderboardSort;
  filter: LeaderboardFilter;
  orgSystemActive: boolean;
  achievementsAvailable: boolean;
}

// ─── State ─────────────────────────────────────────────────────────────────────

const SORT_STORAGE_KEY = 'nt_leaderboard_sort';
const FILTER_STORAGE_KEY = 'nt_leaderboard_filter';

const SORT_LABELS: Record<LeaderboardSort, string> = {
  bounty: 'KOPFGELD',
  level: 'LEVEL',
  achievements: 'ACHIEVEMENTS',
};

const FILTER_LABELS: Record<LeaderboardFilter, string> = {
  all: 'ALLE',
  no_org: 'OHNE ORG',
  seeking: 'SUCHT ORG',
};

let leaderboardData: LeaderboardResponse | null = null;
let lastRefreshTime = 0;
let selectedSort: LeaderboardSort = 'bounty';
let selectedFilter: LeaderboardFilter = 'all';
let loadSeq = 0;
let cooldownInterval: ReturnType<typeof setInterval> | null = null;
const REFRESH_COOLDOWN_MS = 30_000; // 30 Sekunden

function readStored<T extends string>(key: string, allowed: readonly T[], fallback: T): T {
  try {
    const value = localStorage.getItem(key);
    return value !== null && (allowed as readonly string[]).includes(value) ? (value as T) : fallback;
  } catch {
    return fallback;
  }
}

function writeStored(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Speicher blockiert (privates Fenster) — Auswahl gilt dann nur für diese Sitzung.
  }
}

// ─── Lifecycle (Dashboard-Tab) ───────────────────────────────────────────────

/** HTML-Shell für den Dashboard-Tab – wird von renderAll() zurückgegeben */
export function renderCommunityTab(): string {
  return `
    <section class="dash-content" id="dash-community">
      <div class="community-tab-header">
        <h2>RANGLISTE</h2>
        <div class="community-tab-meta">
          <span class="leaderboard-count" id="leaderboard-count">—</span>
          <button id="refresh-leaderboard" class="btn-refresh" disabled>
            AKTUALISIEREN
          </button>
        </div>
      </div>
      <div id="leaderboard-controls" class="leaderboard-controls"></div>
      <div id="leaderboard-content" class="leaderboard-content">
        <p class="loading-text">RANGLISTE WIRD GELADEN...</p>
      </div>
    </section>
  `;
}

/** Nach renderAll() aufrufen: Listener anhängen + Daten laden */
export function mountCommunityTab(): void {
  const player = gameState.getPlayer();
  if (player === null) { navigate('/login'); return; }

  selectedSort = readStored<LeaderboardSort>(SORT_STORAGE_KEY, ['bounty', 'level'], 'bounty');
  selectedFilter = readStored<LeaderboardFilter>(FILTER_STORAGE_KEY, ['all', 'no_org', 'seeking'], 'all');

  attachListeners();
  void loadLeaderboard();
}

/** State zurücksetzen beim Tab-Verlassen */
export function resetCommunityState(): void {
  leaderboardData = null;
  if (cooldownInterval !== null) {
    clearInterval(cooldownInterval);
    cooldownInterval = null;
  }
  // lastRefreshTime absichtlich NICHT zurücksetzen – Cooldown bleibt
}

// ─── Event-Listener ────────────────────────────────────────────────────────────

function attachListeners(): void {
  const refreshBtn = document.getElementById('refresh-leaderboard');
  if (refreshBtn) {
    refreshBtn.addEventListener('click', () => {
      void handleRefresh();
    });
  }

  // Sortierung/Filter — delegiert, da die Leiste bei jedem Laden neu gerendert wird.
  document.getElementById('leaderboard-controls')?.addEventListener('click', (e) => {
    const btn = (e.target as Element).closest<HTMLButtonElement>('button');
    if (!btn) return;
    const sort = btn.dataset.lbSort as LeaderboardSort | undefined;
    const filter = btn.dataset.lbFilter as LeaderboardFilter | undefined;
    if (sort !== undefined && sort !== selectedSort) {
      selectedSort = sort;
      writeStored(SORT_STORAGE_KEY, sort);
      void loadLeaderboard();
    } else if (filter !== undefined && filter !== selectedFilter) {
      selectedFilter = filter;
      writeStored(FILTER_STORAGE_KEY, filter);
      void loadLeaderboard();
    }
  });

  // Spielername → Steckbrief (NT-STECKBRIEF-01). Delegiert am Container, da die Tabelle bei jedem
  // Laden per innerHTML neu aufgebaut wird.
  document.getElementById('leaderboard-content')?.addEventListener('click', (e) => {
    const btn = (e.target as Element).closest<HTMLElement>('[data-steckbrief-username]');
    const username = btn?.dataset.steckbriefUsername;
    if (username !== undefined) void openSteckbrief(username);
  });
}

async function handleRefresh(): Promise<void> {
  const now = Date.now();
  const timeSinceLastRefresh = now - lastRefreshTime;

  if (timeSinceLastRefresh < REFRESH_COOLDOWN_MS) {
    const remainingSeconds = Math.ceil((REFRESH_COOLDOWN_MS - timeSinceLastRefresh) / 1000);
    alert(`Bitte warte noch ${remainingSeconds} Sekunden.`);
    return;
  }

  await loadLeaderboard();
}

// ─── API-Calls ─────────────────────────────────────────────────────────────────

async function loadLeaderboard(): Promise<void> {
  // Neueste Anfrage gewinnt — ein schneller Tab-Wechsel darf nicht von einer älteren Antwort
  // überschrieben werden.
  const seq = ++loadSeq;
  updateRefreshButton(false);
  renderControls();

  try {
    const response = await api.get<LeaderboardResponse>(
      `/community/leaderboard?sort=${selectedSort}&filter=${selectedFilter}`,
    );
    if (seq !== loadSeq) return;
    leaderboardData = response;
    lastRefreshTime = Date.now();
    renderControls();
    renderLeaderboardTable();
    startRefreshCooldown();
  } catch (error) {
    if (seq !== loadSeq) return;
    console.error('Leaderboard-Fehler:', error);
    // Filter nicht verfügbar (Org-System aus) → auf ALLE zurückfallen statt hängenzubleiben.
    if (selectedFilter !== 'all') {
      selectedFilter = 'all';
      writeStored(FILTER_STORAGE_KEY, 'all');
    }
    showError();
  }
}

// ─── Rendering ─────────────────────────────────────────────────────────────────

function renderControls(): void {
  const el = document.getElementById('leaderboard-controls');
  if (!el) return;

  const sorts: LeaderboardSort[] = ['bounty', 'level'];
  if (leaderboardData?.achievementsAvailable === true) sorts.push('achievements');

  const sortButtons = sorts
    .map((s) => `<button type="button" class="lb-seg${s === selectedSort ? ' is-active' : ''}" data-lb-sort="${s}">${SORT_LABELS[s]}</button>`)
    .join('');

  const filters: LeaderboardFilter[] = ['all', 'no_org', 'seeking'];
  const filterRow = leaderboardData?.orgSystemActive === true
    ? `<div class="lb-seg-row">${filters
      .map((f) => `<button type="button" class="lb-seg lb-seg--small${f === selectedFilter ? ' is-active' : ''}" data-lb-filter="${f}">${FILTER_LABELS[f]}</button>`)
      .join('')}</div>`
    : '';

  el.innerHTML = `<div class="lb-seg-row">${sortButtons}</div>${filterRow}`;
}

function renderLeaderboardTable(): void {
  if (leaderboardData === null) return;

  const countEl = document.getElementById('leaderboard-count');
  if (countEl) {
    countEl.textContent = `[gesamt: ${leaderboardData.totalPlayers} Spieler]`;
  }

  const contentEl = document.getElementById('leaderboard-content');
  if (!contentEl) return;

  const { leaderboard, currentUserEntry } = leaderboardData;
  const isBounty = leaderboardData.sort === 'bounty';
  const showSeeking = leaderboardData.orgSystemActive;

  if (leaderboard.length === 0) {
    contentEl.innerHTML = '<p class="empty-text">NOCH KEINE SPIELER GELISTET</p>';
    return;
  }

  const head = isBounty
    ? `
          <th class="col-rank">#</th>
          <th class="col-name">SPIELER</th>
          <th class="col-bounty">KOPFGELD</th>
          <th class="col-focus">SCHWERPUNKT</th>
          <th class="col-level col-hide-xs">LV</th>
          <th class="col-badges">BADGES</th>`
    : `
          <th class="col-rank">#</th>
          <th class="col-name">SPIELER</th>
          <th class="col-level">LV</th>
          <th class="col-rep">RUF</th>
          <th class="col-wanted">FAHNDUNG</th>
          <th class="col-prestige">PRESTIGE</th>
          <th class="col-badges">BADGES</th>`;

  const renderRow = (entry: LeaderboardEntry, showRankLabel = false): string =>
    isBounty ? renderBountyRow(entry, showSeeking, showRankLabel) : renderLevelRow(entry, showRankLabel);

  let html = `
    <table class="leaderboard-table${isBounty ? ' leaderboard-table--bounty' : ''}">
      <thead>
        <tr>${head}
        </tr>
      </thead>
      <tbody>
  `;

  for (const entry of leaderboard) {
    html += renderRow(entry);
  }

  html += '</tbody></table>';

  // Eigene Position wenn außerhalb Top 50
  if (currentUserEntry !== null) {
    html += '<div class="leaderboard-separator"></div>';
    html += `<table class="leaderboard-table leaderboard-current-user${isBounty ? ' leaderboard-table--bounty' : ''}">
      <tbody>${renderRow(currentUserEntry, true)}</tbody>
    </table>`;
  }

  contentEl.innerHTML = html;
}

function renderNameCell(entry: LeaderboardEntry, showSeeking: boolean): string {
  const seeking = showSeeking && entry.seekingOrg ? ' <span class="lb-seeking">SUCHT ORG</span>' : '';
  return `<button type="button" class="leaderboard-name-link" data-steckbrief-username="${escapeHtml(entry.username)}">${escapeHtml(entry.username)}</button>${seeking}`;
}

function renderBountyRow(entry: LeaderboardEntry, showSeeking: boolean, showRankLabel: boolean): string {
  const rowClass = entry.isCurrentUser ? 'leaderboard-row current-user' : 'leaderboard-row';
  const rankDisplay = showRankLabel ? `DU (Platz ${entry.rank})` : String(entry.rank);
  const bountyDisplay = entry.bounty > 0
    ? formatBounty(entry.bounty)
    : `<span class="lb-blank">${UNBESCHRIEBENES_BLATT}</span>`;
  const focusDisplay = entry.bounty > 0
    ? `<span class="lb-focus-long">${focusLabel(entry.focus)}</span><span class="lb-focus-short">${focusShortLabel(entry.focus)}</span>`
    : '—';

  return `
    <tr class="${rowClass}">
      <td class="col-rank">${rankDisplay}</td>
      <td class="col-name">${renderNameCell(entry, showSeeking)}</td>
      <td class="col-bounty">${bountyDisplay}</td>
      <td class="col-focus">${focusDisplay}</td>
      <td class="col-level col-hide-xs">${entry.level}</td>
      <td class="col-badges">${renderBadges(entry.badges)}</td>
    </tr>
  `;
}

function renderLevelRow(entry: LeaderboardEntry, showRankLabel: boolean): string {
  const rowClass = entry.isCurrentUser ? 'leaderboard-row current-user' : 'leaderboard-row';
  // Behördenname statt Balken + Stufenzahl (NT-FAHNDUNG-02) — die Farbe zeigt die Schwere.
  const wantedDisplay = renderWantedLevelHtml(entry.wantedLevel);
  const prestigeDisplay = renderPrestigeRank(entry.prestigeRank);
  const badgesDisplay = renderBadges(entry.badges);
  const rankDisplay = showRankLabel ? `DU (Platz ${entry.rank})` : entry.rank;

  return `
    <tr class="${rowClass}">
      <td class="col-rank">${rankDisplay}</td>
      <td class="col-name"><button type="button" class="leaderboard-name-link" data-steckbrief-username="${escapeHtml(entry.username)}">${escapeHtml(entry.username)}</button></td>
      <td class="col-level">${entry.level}</td>
      <td class="col-rep">${entry.reputation}</td>
      <td class="col-wanted">${wantedDisplay}</td>
      <td class="col-prestige">${prestigeDisplay}</td>
      <td class="col-badges">${badgesDisplay}</td>
    </tr>
  `;
}

function renderPrestigeRank(rank: number): string {
  if (rank === 0) return '<span class="prestige-0">—</span>';
  if (rank === 1) return '<span class="prestige-1">I</span>';
  if (rank === 2) return '<span class="prestige-2">II</span>';
  if (rank === 3) return '<span class="prestige-3">III</span>';
  return '<span class="prestige-0">—</span>';
}

// Farbe/Priorität kommen bereits fertig komponiert vom Backend (award.definitions.ts::
// resolveBadges, NT-TITLES-01) — max. 2 Badges, hier nur noch reines Rendering.
function renderBadges(badges: AwardBadge[]): string {
  if (badges.length === 0) return '—';
  return badges.map((badge) => awardBadgeHtml(badge, 'badge')).join(' ');
}

// ─── Hilfsfunktionen ───────────────────────────────────────────────────────────

function updateRefreshButton(enabled: boolean): void {
  const btn = document.getElementById('refresh-leaderboard') as HTMLButtonElement | null;
  if (btn) {
    btn.disabled = !enabled;
    btn.textContent = enabled ? 'AKTUALISIEREN' : 'AKTUALISIEREN (...)';
  }
}

function startRefreshCooldown(): void {
  updateRefreshButton(false);
  if (cooldownInterval !== null) clearInterval(cooldownInterval);

  let remainingSeconds = 30;
  const btn = document.getElementById('refresh-leaderboard') as HTMLButtonElement | null;

  cooldownInterval = setInterval(() => {
    remainingSeconds--;
    if (btn) {
      btn.textContent = `AKTUALISIEREN (${remainingSeconds}s)`;
    }

    if (remainingSeconds <= 0) {
      if (cooldownInterval !== null) clearInterval(cooldownInterval);
      cooldownInterval = null;
      updateRefreshButton(true);
    }
  }, 1000);
}

function showError(): void {
  const contentEl = document.getElementById('leaderboard-content');
  if (contentEl) {
    contentEl.innerHTML = '<p class="error-text">RANGLISTE NICHT VERFÜGBAR</p>';
  }
}

function escapeHtml(text: string): string {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}
